import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/pinia/auth'

// Confirmed with BE 2026-09-04: every request to the gateway made while in an org-flow session
// (the shared `digitalbuildings` realm) needs an `Organization-ID` header, on every service, not
// just core-service -- without it, requests 400. Read straight from localStorage rather than
// `useOrganizationStore()` to avoid a circular import (pinia/organization.ts ->
// services/organizations.service.ts -> services/api.service.ts -> this file); the store already
// persists the key under this exact key on every selection (see setCurrentOrganization). Empty
// for every normal per-customer realm-flow session, so this is a no-op there. Two OTHER entry
// points that also talk to the gateway need the same header (added separately, same pattern):
// src/http-client/models/HttpClient.axios.ts and src/composables/useFetch.ts's
// createAxiosClient(). HTTPKeyCloakClient (Keycloak itself) is the only client here that
// deliberately does NOT get it.
function withOrganizationHeader(headers: Record<string, unknown>) {
  const organizationKey = localStorage.getItem('organizationKey')
  if (organizationKey) headers['Organization-ID'] = organizationKey
  return headers
}

// Error handler shared by every request interceptor below: pass the error on unchanged, so the
// caller's own .catch() / try-catch still sees it.
function rejectRequest(err: Error) {
  return Promise.reject(err)
}

const HTTPClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPGatewayClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPKeyCloakClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL,
  timeout: 30000
})

const HTTPFileClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL + '/file',
  timeout: 30000
})

// Client for file uploads. Same gateway route as HTTPFileClient, but without a client-side timeout:
// large uploads can take minutes, and the api ingress gives /file/ a 600s timeout of its own
// (ZDB-7501). Going through the gateway means the organization is resolved there, like every other call.
const HTTPFileServiceClient: AxiosInstance = axios.create({
  baseURL: globalThis.appConfig.VITE_API_BASE_URL + '/file',
  timeout: 0
})

HTTPClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore()
  config.headers = config.headers || {}
  config.headers['Accept'] = 'application/json'
  config.headers['Content-Type'] = 'application/json'
  config.headers['Authorization'] = `Bearer ${accessToken}`
  withOrganizationHeader(config.headers)
  return config
}, rejectRequest)

HTTPGatewayClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore()
  config.headers = config.headers || {}
  config.headers['Accept'] = config.headers['Accept'] || 'application/json'
  config.headers['Authorization'] = `Bearer ${accessToken}`
  withOrganizationHeader(config.headers)
  return config
}, rejectRequest)

HTTPFileClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore()
  config.headers = config.headers || {}
  config.headers['Authorization'] = `Bearer ${accessToken}`
  withOrganizationHeader(config.headers)
  return config
}, rejectRequest)

HTTPFileServiceClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore()
  config.headers = config.headers || {}
  config.headers['Authorization'] = `Bearer ${accessToken}`
  withOrganizationHeader(config.headers)
  return config
}, rejectRequest)

HTTPKeyCloakClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore()
  config.headers = config.headers || {}
  config.headers['Authorization'] = `Bearer ${accessToken}`
  return config
}, rejectRequest)

export { HTTPClient, HTTPFileClient, HTTPGatewayClient, HTTPFileServiceClient, HTTPKeyCloakClient }
